import { gql, useQuery, useLazyQuery, QueryHookOptions } from "@apollo/client";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";
import { Server } from "../../../apollo/types";
import { appointmentInfoAtom } from "../../../state";
import { withRetry } from "../../../utils";
import { useCallback, useMemo } from "react";
import { logger } from "../../../datadog";
import { useQueryRetryConfig } from "../../../useQueryRetryConfig";

export type ClientRequiresAuthedIntroRoomQuery = {
  clientRequiresAuthedIntroRoom: {
    requireAuth: boolean;
    clientUserShortId: string | null;
    isMinor: boolean;
  };
  publicPatient: {
    customerId: string;
    isEvernorthPatient: boolean;
  };
};

export type ClientRequiresAuthedIntroRoomVariables = {
  patientShortId: string;
  patientShortIdForPublicPatient: string;
  appointmentShortId?: string;
};

/**
 * Helper to create variables for the CLIENT_REQUIRES_AUTHED_INTRO_ROOM query.
 * Handles the type conversion needed because clientRequiresAuthedIntroRoom expects String!
 * while publicPatient expects ID! (both are strings at runtime).
 */
export function createClientRequiresAuthedIntroRoomVariables(
  patientShortId: string | undefined,
  appointmentShortId: string | undefined,
): ClientRequiresAuthedIntroRoomVariables | undefined {
  if (!patientShortId) return undefined;
  return {
    patientShortId,
    patientShortIdForPublicPatient: patientShortId,
    appointmentShortId,
  };
}

export const CLIENT_REQUIRES_AUTHED_INTRO_ROOM = gql`
  query ClientRequiresAuthedIntroRoom(
    $patientShortId: String!
    $patientShortIdForPublicPatient: ID!
    $appointmentShortId: ID
  ) {
    clientRequiresAuthedIntroRoom(
      patientShortId: $patientShortId
      appointmentShortId: $appointmentShortId
    ) {
      requireAuth
      clientUserShortId
      isMinor
    }
    publicPatient(shortId: $patientShortIdForPublicPatient) {
      customerId
      isEvernorthPatient
    }
  }
`;

/**
 * Hook for fetching ClientRequiresAuthedIntroRoom.
 *
 * This query indicates whether or not a client needs to take extra steps (e.g.
 * signing any missing required legal consents) before they can be allowed to
 * enter the waiting room; the check is referred to as "requiring auth" because
 * historically, signing consents required that users were first authenticated,
 * and telehealth does not require users to be authenticated to join sessions
 * under normal circumstances.
 *
 * @param opts - Optional options to pass to the `useQuery` hook.
 * @returns The return value of the `useQuery` hook
 */
export function useGetClientRequiresAuthedIntroRoom(
  opts: QueryHookOptions<
    ClientRequiresAuthedIntroRoomQuery,
    ClientRequiresAuthedIntroRoomVariables
  > = {},
) {
  const { patientShortId } = useParams();
  const appointmentInfo = useAtomValue(appointmentInfoAtom);
  const appointmentShortId = appointmentInfo?.shortId;

  const variables = useMemo(
    () =>
      createClientRequiresAuthedIntroRoomVariables(
        patientShortId,
        appointmentShortId,
      ),
    [patientShortId, appointmentShortId],
  );

  return useQuery<
    ClientRequiresAuthedIntroRoomQuery,
    ClientRequiresAuthedIntroRoomVariables
  >(CLIENT_REQUIRES_AUTHED_INTRO_ROOM, {
    variables,
    skip: !patientShortId || opts.skip,
    context: { server: Server.MONOLITH, skipAuth: true, ...opts.context },
    onError: (error) => {
      logger.warn(
        "Failed to fetch getClientRequiresAuthedIntroRoom",
        { patientShortId },
        error,
      );
      opts.onError?.(error);
    },
    ...opts,
  });
}

export type UseLazyClientRequiresAuthedIntroRoomDependencies = {
  useRetryConfig: typeof useQueryRetryConfig;
};

/**
 * Hook that returns a callback to lazily query ClientRequiresAuthedIntroRoom.
 *
 * This query indicates whether or not a client needs to take extra steps (e.g.
 * signing any missing required legal consents) before they can be allowed to
 * enter the waiting room; the check is referred to as "requiring auth" because
 * historically, signing consents required that users were first authenticated,
 * and telehealth does not require users to be authenticated to join sessions
 * under normal circumstances.
 *
 * If the request fails, the callback will retry up to `retryConfig.retries`
 * times with exponential backoff. If after `retryConfig.retries` attempts, the
 * request still fails, the error is logged and the client should be allowed to
 * enter the waiting room.
 *
 * @param dependencies - Optional dependency overrides (e.g. for testing)
 */
export function useLazyClientRequiresAuthedIntroRoom(
  { useRetryConfig }: UseLazyClientRequiresAuthedIntroRoomDependencies = {
    useRetryConfig: useQueryRetryConfig,
  },
) {
  const { patientShortId } = useParams();
  const retryConfig = useRetryConfig();

  const [doQuery] = useLazyQuery<
    ClientRequiresAuthedIntroRoomQuery,
    ClientRequiresAuthedIntroRoomVariables
  >(CLIENT_REQUIRES_AUTHED_INTRO_ROOM, {
    context: { server: Server.MONOLITH, skipAuth: true },
  });

  const getClientRequiresAuthedIntroRoom: typeof doQuery = useCallback(
    async (options) => {
      const result = await doQuery(options);
      // withRetry only retries when an error is thrown
      if (result.error) {
        throw {
          ...result.error,
          patientShortId: options?.variables?.patientShortId,
        };
      }
      if (!result.data?.clientRequiresAuthedIntroRoom) {
        throw {
          patientShortId: options?.variables?.patientShortId,
          message: "No data returned from query",
        };
      }
      return result;
    },
    [doQuery],
  );

  return useMemo(
    () =>
      withRetry(getClientRequiresAuthedIntroRoom, {
        ...retryConfig,
        onFailure: ({ error, ...details }) => {
          logger.warn(
            "Failed to fetch getClientRequiresAuthedIntroRoom with retry",
            { patientShortId, ...details },
            error,
          );
        },
      }),
    [getClientRequiresAuthedIntroRoom, patientShortId, retryConfig],
  );
}
